import { IHttpService } from "../HttpService";
import { formatBooleanValue, parseBooleanValue } from "../..";
import MenuItem, { IMenuItem } from "../../model/menu/MenuItem";

export interface IMenuItemService {
  getAllRootMenuItems(
    aplModCode?: string,
    showOnlyValid?: boolean
  ): Promise<IMenuItem[]>;
  getAvailableMenuItems(menuItem?: IMenuItem): Promise<IMenuItem[]>;
  createMenuItem(menuItem: IMenuItem): Promise<any>;
  updateMenuItem(menuItem: IMenuItem): any;
}

const MenuItemService = (httpService): IMenuItemService => {
  const _http: IHttpService = httpService;
  const _baseUrl: string = "/api/codebooks";
  const _menuItemsPath = "menu-items";

  let MenuItemListMapper = (json = []) => {
    return json.map((item: any) => {
      let menuItem = MenuItem(item);
      menuItem.valid = formatBooleanValue(item.valid);
      return menuItem;
    });
  };

  let ParamsMapper = (menuItem: IMenuItem) => {
    return {
      id: menuItem.id,
      name: menuItem.name,
      url: menuItem.url,
      valid: parseBooleanValue(menuItem.valid),
      description: menuItem.description,
      menuItemId: menuItem.menuItemId,
      aplModCode: menuItem.aplModCode,
      rbr: menuItem.rbr
    };
  };

  function traverseMenuItems(menuItem: IMenuItem) {
    let visitedMenuItems: IMenuItem[] = [menuItem];

    if (menuItem.children) {
      menuItem.children.forEach((item) => {
        visitedMenuItems = [...visitedMenuItems, ...traverseMenuItems(item)];
      });
    }

    return visitedMenuItems;
  }

  function populateMenuItemChildren(
    menuItem: IMenuItem,
    menuItems: IMenuItem[]
  ) {
    const menuItemChildren = menuItems.filter(
      (item) => item.menuItemId === menuItem.id
    );

    if (menuItemChildren && menuItemChildren.length > 0) {
      menuItem.children = menuItemChildren;

      menuItem.children.forEach((item) =>
        populateMenuItemChildren(item, menuItems)
      );
    }
  }

  return {
    async getAllRootMenuItems(aplModCode?: string, showOnlyValid?: boolean) {
      const path = _http.buildPath(_baseUrl, _menuItemsPath);
      const response = await _http.get(path, {
        params: {
          aplModCode,
          showOnlyValid: showOnlyValid
            ? parseBooleanValue(showOnlyValid)
            : undefined
        }
      });
      const responseJSON = await _http.toJSON(response);
      let menuItems = MenuItemListMapper(responseJSON);

      let rootMenuItems = menuItems.filter((menuItem) => !menuItem.menuItemId);
      rootMenuItems.forEach((menuItem) =>
        populateMenuItemChildren(menuItem, menuItems)
      );

      return rootMenuItems;
    },

    async getAvailableMenuItems(menuItem?: IMenuItem) {
      const path = _http.buildPath(_baseUrl, _menuItemsPath);
      const response = await _http.get(path);
      const responseJSON = await _http.toJSON(response);

      let menuItems = MenuItemListMapper(responseJSON).filter(
        (item) => !(menuItem && item.id === menuItem.id)
      );

      let rootMenuItems = menuItems.filter((menuItem) => !menuItem.menuItemId);

      rootMenuItems.forEach((menuItem) =>
        populateMenuItemChildren(menuItem, menuItems)
      );

      let resultingMenuItems: IMenuItem[] = [];

      rootMenuItems.forEach((rootMenuItem) => {
        resultingMenuItems = [
          ...resultingMenuItems,
          ...traverseMenuItems(rootMenuItem)
        ];
      });

      return resultingMenuItems;
    },

    async createMenuItem(menuItem: IMenuItem) {
      const path = _http.buildPath(_baseUrl, _menuItemsPath);
      const response = await _http.post(path, {
        params: ParamsMapper(menuItem)
      });
      return _http.toText(response);
    },

    async updateMenuItem(menuItem: IMenuItem) {
      const path = _http.buildPath(
        _baseUrl,
        _menuItemsPath,
        menuItem.id.toString()
      );
      const response = await _http.put(path, {
        params: ParamsMapper(menuItem)
      });
      return _http.toText(response);
    }
  };
};

export default MenuItemService;
